/**
 * Theme preference handling (issue #304).
 *
 * The app has two visual themes driven entirely by CSS tokens in index.css,
 * selected by the `data-theme` attribute on <html>. The *preference* is
 * three-way — 'system' follows `prefers-color-scheme`, 'light'/'dark' are
 * explicit overrides persisted to localStorage. index.html contains a
 * pre-paint copy of the resolve logic (same storage key, same meta colors) so
 * the first frame is already correct; this module owns everything after that.
 *
 * Storage key migration (#470, Clara rebrand): the key was renamed from
 * 'reconflux-theme' to 'clara-theme'. `getThemePreference` migrates a valid
 * legacy value on first read (copy forward, drop the old key) so existing
 * users keep their preference. index.html's pre-paint script performs the
 * same fallback read — keep both in sync.
 */

export type ThemePreference = 'system' | 'light' | 'dark'
export type ResolvedTheme = 'light' | 'dark'

export const THEME_STORAGE_KEY = 'clara-theme'
/** Pre-rebrand key (#470) — read once to migrate an existing preference. */
const LEGACY_THEME_STORAGE_KEY = 'reconflux-theme'

/** Browser-chrome color per theme — mirrors --clara-surface-page in index.css. */
export const META_THEME_COLORS: Record<ResolvedTheme, string> = {
  dark: '#080809',
  light: '#FAFAF8',
}

const listeners = new Set<() => void>()

export function getThemePreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY)
    if (stored === 'light' || stored === 'dark') return stored

    // One-time migration (#470): adopt a valid legacy value if the new key
    // hasn't been written yet, then drop the old key so this only runs once.
    const legacy = localStorage.getItem(LEGACY_THEME_STORAGE_KEY)
    if (legacy === 'light' || legacy === 'dark') {
      localStorage.setItem(THEME_STORAGE_KEY, legacy)
      localStorage.removeItem(LEGACY_THEME_STORAGE_KEY)
      return legacy
    }
  } catch {
    /* storage blocked — treat as system */
  }
  return 'system'
}

export function resolveTheme(preference: ThemePreference): ResolvedTheme {
  if (preference === 'light' || preference === 'dark') return preference
  // Light is the brand default: 'system' only resolves dark when the OS
  // explicitly asks for it (no matchMedia / no signal -> light).
  if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches) {
    return 'dark'
  }
  return 'light'
}

/** Stamp the resolved theme on <html> and keep the browser chrome in sync. */
function applyTheme(theme: ResolvedTheme): void {
  document.documentElement.dataset.theme = theme
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', META_THEME_COLORS[theme])
}

export function setThemePreference(preference: ThemePreference): void {
  try {
    if (preference === 'system') {
      localStorage.removeItem(THEME_STORAGE_KEY)
    } else {
      localStorage.setItem(THEME_STORAGE_KEY, preference)
    }
    // An older PWA tab can recreate the pre-rebrand key after this tab has
    // migrated it. Always clear that fallback when the user makes a choice;
    // otherwise selecting "system" can immediately remigrate a stale value.
    localStorage.removeItem(LEGACY_THEME_STORAGE_KEY)
  } catch {
    /* storage blocked — the in-session theme still applies below */
  }
  applyTheme(resolveTheme(preference))
  listeners.forEach((listener) => listener())
}

/** Subscribe to preference changes (for useSyncExternalStore). */
export function subscribeToTheme(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/**
 * Wire up the runtime listeners. Called once from main.tsx. The pre-paint
 * script already stamped the initial theme, so this only handles *changes*:
 * OS preference flips while on 'system', and cross-tab storage updates.
 */
export function initTheme(): void {
  const media = window.matchMedia('(prefers-color-scheme: dark)')
  media.addEventListener('change', () => {
    if (getThemePreference() === 'system') {
      applyTheme(resolveTheme('system'))
      listeners.forEach((listener) => listener())
    }
  })

  window.addEventListener('storage', (event) => {
    if (event.key !== THEME_STORAGE_KEY && event.key !== null) return
    applyTheme(resolveTheme(getThemePreference()))
    listeners.forEach((listener) => listener())
  })
}
